import { Button, Text } from '@react-spectrum/s2';
import ChevronRight from '@react-spectrum/s2/icons/ChevronRight';
import {
	useCallback,
	useEffect,
	useRef,
	useState,
	type ChangeEvent as ReactChangeEvent,
	type KeyboardEvent as ReactKeyboardEvent,
	type PointerEvent as ReactPointerEvent,
} from 'react';
import { Form } from 'react-aria-components';

import { cn } from '@/utils/cn';

import { TypewriterOnce } from '../components/TypewriterOnce';

import {
	buildTacticLadder,
	isAllIn,
	NOTCH_LABELS,
	NOTCHES,
	positionToValue,
	snapToNotch,
	valueToPosition,
} from './budgetSlider';

type BudgetScreenProps = {
	// Re-seeds the field when the user navigates back here: the wizard
	// remounts screens on every transition, so local state doesn't survive.
	initialAmount?: number | null;
	onContinue: (monthlyBudgetUsd: number) => void;
	onSkip: () => void;
};

const HEADLINE = "What's your monthly marketing budget?";

const SUBCOPY = 'Your marketing spend, not what you pay for Bridgeway.';

const SLIDER_MAX = NOTCHES[NOTCHES.length - 1];

const prefersReducedMotion = () =>
	typeof window !== 'undefined' &&
	typeof window.matchMedia === 'function' &&
	window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const eyebrowClass =
	'text-[11px] font-semibold tracking-[0.12em] text-(--text-muted) uppercase';

export const BudgetScreen = ({
	initialAmount,
	onContinue,
	onSkip,
}: BudgetScreenProps) => {
	const [amount, setAmount] = useState(
		initialAmount != null && initialAmount > 0 ? String(initialAmount) : '',
	);
	const [typingDone, setTypingDone] = useState(false);
	const handleTypingDone = useCallback(() => setTypingDone(true), []);
	// While the hero field has focus it shows the raw digits the user is
	// typing; when it loses focus it falls back to the formatted, capped
	// readout ("$1,000+") so the resting display is pixel-identical to before.
	const [amountFocused, setAmountFocused] = useState(false);

	// Track position while a drag is in progress; null otherwise. The thumb
	// follows the pointer mid-drag and is derived from `amount` the rest of the
	// time — the slider and arrow keys are the only inputs to the value.
	const [dragPosition, setDragPosition] = useState<number | null>(null);
	const trackRef = useRef<HTMLDivElement>(null);
	const thumbRef = useRef<HTMLDivElement>(null);
	// Element-level pointer handlers captured for the active drag, so the same
	// references can be torn down on pointerup, pointercancel, and unmount.
	const dragCleanup = useRef<(() => void) | null>(null);

	useEffect(() => () => dragCleanup.current?.(), []);

	// An empty field reads as $0 (and routes to skip), matching the slider's
	// resting state — the readout still shows "$0" either way.
	const parsed = amount.trim() === '' ? 0 : Number(amount);
	const isValid = Number.isFinite(parsed) && parsed >= 0;
	const budget = isValid ? parsed : 0;

	const handleAmountChange = useCallback(
		(e: ReactChangeEvent<HTMLInputElement>) => {
			const next = e.target.value;
			// Whole dollars only: digits or empty; anything else is ignored so the
			// field can't be mangled into a non-numeric state.
			if (next === '' || /^\d+$/.test(next)) setAmount(next);
		},
		[],
	);

	const positionFromX = useCallback((clientX: number) => {
		const track = trackRef.current;
		if (!track) return 0;
		const rect = track.getBoundingClientRect();
		return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
	}, []);

	const handleTrackPointerDown = useCallback(
		(e: ReactPointerEvent) => {
			// Secondary buttons / non-primary pointers don't start a drag.
			if (!e.isPrimary || e.button !== 0) return;
			e.preventDefault();

			const thumb = thumbRef.current;
			const pointerId = e.pointerId;
			// Capture on the thumb so move/up/cancel land on one element even when
			// the pointer leaves the track, and focus the thumb for keyboard users.
			thumb?.setPointerCapture?.(pointerId);
			thumb?.focus();

			const apply = (clientX: number) => {
				const p = positionFromX(clientX);
				setDragPosition(p);
				setAmount(String(positionToValue(p)));
			};
			apply(e.clientX);

			const onMove = (me: globalThis.PointerEvent) => {
				if (me.pointerId !== pointerId) return;
				apply(me.clientX);
			};
			const teardown = () => {
				if (!thumb) return;
				thumb.removeEventListener('pointermove', onMove);
				thumb.removeEventListener('pointerup', onUp);
				thumb.removeEventListener('pointercancel', onCancel);
				thumb.releasePointerCapture?.(pointerId);
				dragCleanup.current = null;
			};
			const onUp = (me: globalThis.PointerEvent) => {
				if (me.pointerId !== pointerId) return;
				// Granular while dragging; settle on a marked notch on release.
				const snapped = snapToNotch(positionFromX(me.clientX));
				setAmount(String(positionToValue(snapped)));
				setDragPosition(null);
				teardown();
			};
			const onCancel = (me: globalThis.PointerEvent) => {
				if (me.pointerId !== pointerId) return;
				// Lost pointer (e.g. OS gesture): keep the last value, drop the drag.
				setDragPosition(null);
				teardown();
			};

			thumb?.addEventListener('pointermove', onMove);
			thumb?.addEventListener('pointerup', onUp);
			thumb?.addEventListener('pointercancel', onCancel);
			dragCleanup.current = teardown;
		},
		[positionFromX],
	);

	const handleThumbKeyDown = useCallback(
		(e: ReactKeyboardEvent) => {
			if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
			e.preventDefault();
			if (e.key === 'ArrowLeft') {
				const lower = [...NOTCHES].reverse().find((n) => n < budget);
				setAmount(String(lower ?? NOTCHES[0]));
			} else {
				const higher = NOTCHES.find((n) => n > budget);
				setAmount(String(higher ?? SLIDER_MAX));
			}
		},
		[budget],
	);

	const thumbPosition = dragPosition ?? valueToPosition(budget);
	const ladder = buildTacticLadder(budget);
	const activeCount = ladder.filter((row) => row.state === 'active').length;
	const allIn = isAllIn(budget);
	const reduceMotion = prefersReducedMotion();

	// The top notch is the "$1k+" stop, so reaching it (allIn) reads as "$1,000+"
	// and "all N tactics in play" — consistent with the ladder's all-in cap.
	const displayAmount = Math.min(budget, SLIDER_MAX).toLocaleString();
	// What the hero field renders: the raw digits while the user is typing,
	// otherwise the formatted, capped readout so the resting display is unchanged.
	const heroValue = amountFocused ? amount : displayAmount;
	const ariaValueText = allIn
		? `$${SLIDER_MAX.toLocaleString()}+ per month — all ${ladder.length} of ${ladder.length} tactics in play`
		: `$${budget.toLocaleString()} per month — ${activeCount} of ${ladder.length} tactics in play`;

	return (
		<div
			className="flex h-full w-full items-start justify-center overflow-y-auto
				pt-16 pb-8"
		>
			<div className="w-full max-w-[760px] px-8">
				<h2
					className="mb-2 font-bold text-(--text-primary)"
					style={{
						fontSize: 'clamp(24px, 3.5vw, 34px)',
						letterSpacing: '-0.02em',
						lineHeight: 1.15,
					}}
				>
					<TypewriterOnce text={HEADLINE} onDone={handleTypingDone} />
				</h2>
				<p
					className="mb-6 text-(--text-muted)"
					style={{
						fontSize: 16,
						lineHeight: 1.5,
						opacity: typingDone ? 1 : 0,
						transition: 'opacity 400ms ease',
					}}
				>
					{SUBCOPY}
				</p>

				<div
					style={{
						opacity: typingDone ? 1 : 0,
						transition: 'opacity 400ms ease',
						pointerEvents: typingDone ? 'auto' : 'none',
					}}
				>
					<Form
						className="flex flex-col gap-4"
						onSubmit={(e) => {
							e.preventDefault();
							if (!isValid) return;
							if (parsed === 0) onSkip();
							else onContinue(parsed);
						}}
					>
						{/* Zone 1 — hero amount + slider */}
						<div className="glass-card-sm flex flex-col gap-4 p-5">
							{/* The hero number IS an auto-sizing text field. At rest it shows
							    the formatted, capped readout ("$1,000+") so it's pixel-
							    identical to a plain label; on focus it swaps to the raw
							    digits the user types. Clicking anywhere on the number (the
							    label) focuses the field and places the caret. The aria-hidden
							    sizer span shares the input's font and drives its width. */}
							<label className="flex cursor-text items-baseline gap-2">
								<span
									data-testid="budget-hero-amount"
									className="font-adobe-clean-display inline-flex items-baseline
										font-bold text-(--text-primary)"
									style={{
										fontSize: 'clamp(36px, 5vw, 48px)',
										lineHeight: 1,
										letterSpacing: '-0.02em',
									}}
								>
									<span aria-hidden="true" className="mr-[3px]">
										$
									</span>
									{/* The digits are a real, baseline-aligned span (so they sit on
									    the same baseline as the "$"); the input is overlaid in the
									    same grid cell with transparent text, contributing only the
									    caret and the editing/click target. */}
									<span className="inline-grid">
										<span
											aria-hidden="true"
											className="col-start-1 row-start-1 whitespace-pre"
										>
											{heroValue === '' ? '0' : heroValue}
										</span>
										<input
											aria-label="Monthly budget"
											type="text"
											inputMode="numeric"
											size={1}
											value={heroValue}
											onChange={handleAmountChange}
											onFocus={() => setAmountFocused(true)}
											onBlur={() => setAmountFocused(false)}
											className="col-start-1 row-start-1 w-full min-w-0 border-0
												bg-transparent p-0 leading-[inherit] text-transparent
												caret-(--text-primary) outline-none"
											style={{
												font: 'inherit',
												letterSpacing: 'inherit',
											}}
										/>
									</span>
									{allIn && !amountFocused ? (
										<span aria-hidden="true">+</span>
									) : null}
								</span>
								<span
									data-testid="budget-hero-unit"
									className="text-(--text-muted)"
									style={{ fontSize: 18 }}
								>
									/ month
								</span>
							</label>

							<div className="flex flex-col gap-3">
								<div
									ref={trackRef}
									data-testid="budget-slider-track"
									className="relative h-1 cursor-pointer rounded-full
										bg-(--color-beacon-stroke)"
									style={{ touchAction: 'none' }}
									onPointerDown={handleTrackPointerDown}
								>
									<div
										className="absolute h-full rounded-full bg-(--text-primary)
											opacity-30"
										style={{ left: 0, width: `${thumbPosition * 100}%` }}
									/>
									{/* The cloned in-house sliders ship no aria or keyboard
									    support, so the thumb carries it all itself. */}
									<div
										ref={thumbRef}
										data-testid="budget-slider-thumb"
										role="slider"
										tabIndex={0}
										aria-label="Monthly marketing budget"
										aria-valuemin={0}
										aria-valuemax={SLIDER_MAX}
										aria-valuenow={Math.min(budget, SLIDER_MAX)}
										aria-valuetext={ariaValueText}
										className={cn(
											`absolute top-1/2 size-5 -translate-x-1/2 -translate-y-1/2
											rounded-full border-2 border-(--text-primary) bg-white`,
											dragPosition !== null ? 'cursor-grabbing' : 'cursor-grab',
										)}
										style={{
											left: `${thumbPosition * 100}%`,
											touchAction: 'none',
										}}
										onPointerDown={(e) => {
											e.stopPropagation();
											handleTrackPointerDown(e);
										}}
										onKeyDown={handleThumbKeyDown}
									/>
								</div>
								<div className="relative" style={{ height: 16 }}>
									{NOTCH_LABELS.map((label, i) => (
										<span
											key={label}
											className="absolute text-[12px] text-(--text-muted)"
											style={{
												left: `${(i / (NOTCH_LABELS.length - 1)) * 100}%`,
												transform: 'translateX(-50%)',
											}}
										>
											{label}
										</span>
									))}
								</div>
							</div>
						</div>

						{/* Zone 2 — cumulative unlock ladder */}
						<div className="glass-card-sm flex flex-col gap-2 p-5">
							<span className={eyebrowClass}>
								{allIn
									? "You're all in"
									: "What I'll consider for your business"}
							</span>
							<ul className="flex flex-col" style={{ margin: 0, padding: 0 }}>
								{ladder.map((row) => {
									const active = row.state === 'active';
									return (
										<li
											key={row.label}
											aria-disabled={!active}
											data-testid="budget-ladder-row"
											data-state={row.state}
											data-frontier={row.isFrontier ? 'true' : 'false'}
											className="flex items-start gap-3 py-1.5"
											style={{
												opacity: active ? 1 : 0.55,
												transition: reduceMotion
													? undefined
													: 'opacity 150ms ease',
											}}
										>
											<span
												aria-hidden="true"
												className="flex shrink-0 items-center justify-center"
												style={{ height: '22.5px' }}
											>
												<span
													className={cn(
														'flex size-4 items-center justify-center',
														row.isFrontier ? 'rounded-[3px]' : 'rounded-full',
														active
															? row.isFrontier
																? `border-2 border-(--color-beacon-accent)
																	bg-(--color-beacon-accent)`
																: `border-2 border-(--text-primary)
																	bg-(--text-primary)`
															: 'border-2 border-(--text-muted) bg-transparent',
													)}
													style={
														row.isFrontier
															? { transform: 'rotate(45deg)' }
															: undefined
													}
												/>
											</span>
											<span
												style={{
													fontSize: 15,
													lineHeight: 1.5,
													fontWeight: row.isFrontier ? 600 : 400,
													color: active
														? 'var(--text-primary)'
														: 'var(--text-muted)',
												}}
											>
												{row.label}
											</span>
										</li>
									);
								})}
							</ul>
						</div>

						<div>
							<Button
								variant="primary"
								staticColor="black"
								fillStyle="fill"
								size="L"
								type="submit"
								isDisabled={!isValid}
							>
								<Text>Lock it in</Text>
								<ChevronRight />
							</Button>
						</div>

						<div>
							<button
								type="button"
								onClick={onSkip}
								className="cursor-pointer text-[14px] text-(--text-muted)
									underline"
								style={{ background: 'none', border: 'none', padding: 0 }}
							>
								Skip — I have no paid budget for now
							</button>
						</div>
					</Form>
				</div>
			</div>
		</div>
	);
};
